import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Camera, 
  Upload, 
  Loader2, 
  AlertTriangle, 
  CheckCircle2, 
  Sparkles,
  Bug,
  Leaf,
  Droplets,
  X,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Composant d'analyse d'image IA pour les cultures
 * Détecte maladies, carences, ravageurs à partir d'une photo
 */
export const PlantAnalysisDialog = ({ 
  parcelle, 
  culture,
  trigger,
  onAnalysisComplete,
  isOpen: controlledOpen,
  onOpenChange: setControlledOpen
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = setControlledOpen || setInternalOpen;
  
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Veuillez sélectionner une image');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image trop volumineuse (max 10 Mo)');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setResult(null);
    }
  };

  const handleAnalyze = async () => {
    if (!imageFile) {
      toast.error('Veuillez d\'abord sélectionner une image');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      if (culture) formData.append('culture', culture);
      if (description) formData.append('description', description);
      if (parcelle?.id) formData.append('parcelle_id', parcelle.id);

      const response = await api.post('/ai-plant/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResult(response.data);
      toast.success('Analyse terminée !');
      
      if (onAnalysisComplete) {
        onAnalysisComplete(response.data);
      }
    } catch (error) {
      console.error('Erreur analyse:', error);
      toast.error('Erreur lors de l\'analyse. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setImageFile(null);
    setImagePreview(null);
    setDescription('');
    setResult(null);
  };

  const getUrgenceColor = (urgence) => {
    switch (urgence?.toLowerCase()) {
      case 'critique': return 'bg-red-500';
      case 'haute': return 'bg-orange-500';
      case 'normale': return 'bg-yellow-500';
      default: return 'bg-green-500';
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'maladie': return <Bug className="w-5 h-5 text-red-500" />;
      case 'carence': return <Droplets className="w-5 h-5 text-yellow-500" />;
      case 'ravageur': return <Bug className="w-5 h-5 text-orange-500" />;
      default: return <Leaf className="w-5 h-5 text-green-500" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <Sparkles className="w-4 h-4" />
            Analyser une photo
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Analyse IA de culture
            {parcelle && (
              <Badge variant="secondary">{parcelle.nom}</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-4 py-2">
            {/* Zone d'upload */}
            {!imagePreview ? (
              <div className="border-2 border-dashed rounded-xl p-8 text-center space-y-4">
                <div className="flex justify-center gap-4">
                  <Button
                    variant="outline"
                    size="lg"
                    className="h-24 w-32 flex-col gap-2"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-8 h-8" />
                    Galerie
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="h-24 w-32 flex-col gap-2"
                    onClick={() => cameraInputRef.current?.click()}
                  >
                    <Camera className="w-8 h-8" />
                    Photo
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Prenez une photo ou sélectionnez une image de votre culture
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>
            ) : (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Aperçu"
                  className="w-full max-h-64 object-contain rounded-lg border"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-8 w-8"
                  onClick={resetForm}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}

            {/* Description optionnelle */}
            {imagePreview && !result && (
              <div className="space-y-2">
                <Label>Description (optionnel)</Label>
                <Textarea
                  placeholder="Décrivez ce que vous observez : feuilles jaunies, taches, insectes..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                />
              </div>
            )}

            {/* Bouton d'analyse */}
            {imagePreview && !result && (
              <Button
                className="w-full h-12 text-lg"
                onClick={handleAnalyze}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Analyser l'image
                  </>
                )}
              </Button>
            )}

            {/* Résultat de l'analyse */}
            {result && (
              <div className="space-y-4">
                {/* Diagnostic principal */}
                <Card className={cn(
                  "border-2",
                  result.urgence === 'critique' && "border-red-500 bg-red-50 dark:bg-red-950/20",
                  result.urgence === 'haute' && "border-orange-500 bg-orange-50 dark:bg-orange-950/20",
                  result.urgence === 'normale' && "border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20",
                  (!result.urgence || result.urgence === 'faible') && "border-green-500 bg-green-50 dark:bg-green-950/20"
                )}>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(result.type_probleme)}
                        <span>{result.nom_probleme || result.diagnostic}</span>
                      </div>
                      <Badge className={cn(getUrgenceColor(result.urgence), "text-white")}>
                        {result.urgence || 'Normal'}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm mb-2">{result.description}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4" />
                      Confiance: {result.confiance || 'Moyenne'}
                    </div>
                  </CardContent>
                </Card>

                {/* Traitements recommandés */}
                {result.traitements_recommandes?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Droplets className="w-4 h-4 text-blue-500" />
                        Traitements recommandés
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {result.traitements_recommandes.map((t, i) => (
                        <div key={i} className="p-3 bg-muted rounded-lg">
                          <p className="font-medium">{t.produit || t.nom}</p>
                          {t.dose && <p className="text-sm text-muted-foreground">Dose: {t.dose}</p>}
                          {t.application && <p className="text-sm text-muted-foreground">{t.application}</p>}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Prévention */}
                {result.prevention?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-500" />
                        Mesures préventives
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        {result.prevention.map((p, i) => (
                          <li key={i}>{p}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Boutons d'action */}
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={resetForm}>
                    Nouvelle analyse
                  </Button>
                  <Button className="flex-1" onClick={() => setOpen(false)}>
                    Fermer
                  </Button>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default PlantAnalysisDialog;
